/**
 * Marquee Text Module
 *
 * Converts a WordPress list block carrying the class `marquee-text` into a
 * continuously scrolling CSS marquee at runtime. No Swiper — the animation is
 * driven entirely by a CSS `@keyframes` rule on `.marquee-text-track`.
 *
 * The track is built from two identical sets of the original `<li>` items.
 * When the track has translated left by exactly half its width (-50%) the
 * browser loops it back to 0, creating a seamless infinite scroll.
 *
 * Expected markup:
 *   <ul class="wp-block-list marquee-text">
 *     <li>ROBATA</li>
 *     <li>OMAKASE</li>
 *   </ul>
 *
 * @package EVT Restaurant Builder
 * @since 1.0.0
 */

/**
 * Initialise all `ul.marquee-text` instances on the page.
 */
export function initMarqueeText() {
	const lists = document.querySelectorAll( 'ul.marquee-text' );

	if ( ! lists.length ) {
		return;
	}

	lists.forEach( ( list ) => {
		const items = Array.from( list.querySelectorAll( ':scope > li' ) );

		if ( ! items.length ) {
			return;
		}

		// Outer wrapper — clips the overflowing track.
		// Transfer inline style and any extra classes from the original <ul> so
		// custom CSS set in the block editor (colour, spacing, etc.) is preserved.
		const wrapper = document.createElement( 'div' );
		wrapper.classList.add( 'marquee-text-wrapper' );

		if ( list.getAttribute( 'style' ) ) {
			wrapper.setAttribute( 'style', list.getAttribute( 'style' ) );
		}

		list.classList.forEach( ( cls ) => {
			if ( cls !== 'wp-block-list' && cls !== 'marquee-text' ) {
				wrapper.classList.add( cls );
			}
		} );

		// Scrolling track — animated via CSS.
		const track = document.createElement( 'div' );
		track.classList.add( 'marquee-text-track' );

		// Helper: build one full set of items as inline spans.
		function buildSet( hidden ) {
			const set = document.createElement( 'div' );
			set.classList.add( 'marquee-text-set' );
			if ( hidden ) {
				set.setAttribute( 'aria-hidden', 'true' );
			}
			items.forEach( ( item ) => {
				const span = document.createElement( 'span' );
				span.classList.add( 'marquee-text-item' );

				// Carry over any classes set on the <li> in the block editor.
				item.classList.forEach( ( cls ) => span.classList.add( cls ) );

				// Carry over any inline styles set on the <li>.
				if ( item.getAttribute( 'style' ) ) {
					span.setAttribute( 'style', item.getAttribute( 'style' ) );
				}

				span.innerHTML = item.innerHTML;
				set.appendChild( span );
			} );
			return set;
		}

		// Two identical sets — animating to -50% of total width loops seamlessly.
		track.appendChild( buildSet( false ) );
		track.appendChild( buildSet( true ) );

		wrapper.appendChild( track );

		// Swap the original <ul> for the marquee wrapper.
		list.parentNode.insertBefore( wrapper, list );
		list.remove();
	} );
}
